import { useEffect, useState, type CSSProperties } from "react";
import classnames from "classnames";
import {
  CollectionIcon,
  LightBulbIcon,
  UsersIcon,
} from "@heroicons/react/outline";
import { APP_ROUTES } from "const";
import { useLocation } from "react-router";
import { Link } from "react-router-dom";

interface INavItem {
  id: string;
  label: string;
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const NAV_ITEMS: INavItem[] = [
  {
    id: "issues",
    label: "Issues",
    href: APP_ROUTES.ISSUES,
    Icon: CollectionIcon,
  },
  {
    id: "solutions",
    label: "Solutions",
    href: APP_ROUTES.SOLUTION_LIST,
    Icon: LightBulbIcon,
  },
  {
    id: "action-groups",
    label: "Action Groups",
    href: APP_ROUTES.ACTION_GROUP_LIST,
    Icon: UsersIcon,
  },
];

function isActivePath(currentPath: string, href: string) {
  if (currentPath === href) {
    return true;
  }

  const normalizedHref = href.endsWith("/") ? href.slice(0, -1) : href;
  return currentPath.startsWith(`${normalizedHref}/`);
}

export interface ICrossDiscoveryNavProps {
  className?: string;
}

export function CrossDiscoveryNav({ className }: ICrossDiscoveryNavProps) {
  const { pathname } = useLocation();
  const [showDesktopDock, setShowDesktopDock] = useState(false);
  const [showMobileDock, setShowMobileDock] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const handleScroll = () => {
      setShowDesktopDock(window.scrollY > 240);
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const footerSentinel = document.getElementById("footer-sentinel");
    if (!footerSentinel || !("IntersectionObserver" in window)) {
      setShowMobileDock(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        setShowMobileDock(!entry.isIntersecting);
      },
      { root: null, threshold: 0 }
    );

    observer.observe(footerSentinel);

    return () => {
      observer.disconnect();
    };
  }, []);

  const mobileSafeAreaStyle: CSSProperties = {
    paddingBottom: "calc(env(safe-area-inset-bottom, 0px))",
  };

  return (
    <div className={classnames("w-full", className)}>
      <div className="hidden justify-center md:flex">
        <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/80 px-3 py-1.5 shadow-lg shadow-slate-200/60 backdrop-blur supports-[backdrop-filter]:backdrop-blur-md">
          {NAV_ITEMS.map(({ href, Icon, id, label }) => {
            const active = isActivePath(pathname, href);
            return (
              <Link
                key={id}
                to={href}
                className={classnames(
                  "group inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all",
                  active
                    ? "bg-gradient-to-r from-sky-500 to-indigo-500 text-white shadow"
                    : "text-slate-600 hover:text-slate-900"
                )}
              >
                <Icon
                  className={classnames(
                    "h-4 w-4",
                    active ? "text-white" : "text-slate-400 group-hover:text-slate-500"
                  )}
                />
                <span className="whitespace-nowrap">{label}</span>
              </Link>
            );
          })}
        </div>
      </div>

      <div
        className={classnames(
          "pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center transition-opacity duration-150 md:hidden",
          showMobileDock ? "opacity-100" : "opacity-0"
        )}
        style={mobileSafeAreaStyle}
        aria-hidden={!showMobileDock}
      >
        <nav className="pointer-events-auto flex w-full items-center justify-around bg-slate-900/95 px-6 py-3 text-slate-100 shadow-[0_-16px_40px_-20px_rgba(15,23,42,0.75)]">
          {NAV_ITEMS.map(({ href, Icon, id, label }) => {
            const active = isActivePath(pathname, href);
            return (
              <Link
                key={id}
                to={href}
                className={classnames(
                  "flex flex-col items-center gap-1 text-xs font-semibold transition",
                  active
                    ? "text-white"
                    : "text-slate-300 hover:text-white"
                )}
              >
                <span
                  className={classnames(
                    "flex h-10 w-10 items-center justify-center rounded-full border",
                    active
                      ? "border-white/40 bg-indigo-500 text-white"
                      : "border-transparent bg-slate-800 text-slate-300"
                  )}
                >
                  <Icon className="h-5 w-5" />
                </span>
                <span className="whitespace-nowrap">{label}</span>
              </Link>
            );
          })}
        </nav>
      </div>

      {showDesktopDock && (
        <div className="pointer-events-none hidden md:block fixed inset-x-0 bottom-6 z-30">
          <div className="pointer-events-auto mx-auto w-full max-w-4xl px-4">
            <nav className="relative grid grid-cols-3 items-center divide-x divide-slate-200 overflow-hidden rounded-3xl border border-slate-200 bg-white/95 shadow-2xl shadow-slate-400/40">
              <span className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-sky-400 via-indigo-500 to-purple-500" />
              {NAV_ITEMS.map(({ href, Icon, id, label }) => {
                const active = isActivePath(pathname, href);
                return (
                  <Link
                    key={id}
                    to={href}
                    className={classnames(
                      "flex flex-col items-center gap-2 px-6 py-5 text-sm font-semibold transition",
                      active
                        ? "bg-slate-50 text-indigo-600"
                        : "text-slate-600 hover:bg-slate-50"
                    )}
                  >
                    <span
                      className={classnames(
                        "flex h-11 w-11 items-center justify-center rounded-full border",
                        active
                          ? "border-indigo-200 bg-indigo-100"
                          : "border-slate-200"
                      )}
                    >
                      <Icon
                        className={classnames(
                          "h-5 w-5",
                          active ? "text-indigo-600" : "text-slate-400"
                        )}
                      />
                    </span>
                    {label}
                  </Link>
                );
              })}
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}

export default CrossDiscoveryNav;
